import { Alert, Button, DialogActions, DialogContent, Stack, Typography } from "@mui/material";
import { CustomDialog, CustomDialogTitle } from "component/CustomDialog";
import { CustomSwitch } from "component/CustomSwitch";
import { ErrorAlert } from "component/ErrorAlert";
import { useState } from "react";
import { COLORS } from "style";
import { HospitalRouteItem, RecommendationWithRelated } from "type/model/api";
import { DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { SortableHospitalRouteItem } from "./SortableHospitalRouteItem";
import isNil from "lodash/isNil";
import { ReadonlyHospitalRouteItem } from "./ReadonlyHospitalRouteItem";
import { getApiErrorMessage } from "utility/error-message";

const getRouteInfo = (item: HospitalRouteItem): string => {
  if (item.action === "pickup") {
    return `Pickup ${item.task.patient_identifier} from ${item.hospital.name}`;
  }
  return `Drop off ${item.task.patient_identifier} at ${item.hospital.name}`;
};

const validateRouteItems = (hospitalRouteItems: HospitalRouteItem[]): string | null => {
  const patientStatus = new Map<string, "pickup" | "delivery">();

  for (const item of hospitalRouteItems) {
    const patientId = item.task.patient_identifier;

    if (item.action === "pickup") {
      if (patientStatus.get(patientId) === "pickup") {
        return `Patient ${patientId} is being picked up again before being dropped off`;
      }
      patientStatus.set(patientId, "pickup");
    } else {
      if (patientStatus.get(patientId) !== "pickup") {
        return `Patient ${patientId} is being dropped off before being picked up`;
      }
      patientStatus.set(patientId, "delivery");
    }
  }

  return null;
};

interface Props {
  recommendation: RecommendationWithRelated;
  onConfirm(
    recommendation: RecommendationWithRelated,
    modifiedHospitalRouteItems?: HospitalRouteItem[] | null
  ): Promise<void>;
  onClose(): void;
}

interface HospitalRouteItemWithId extends HospitalRouteItem {
  id: number;
}

const ModifiableHospitalRouteItems = ({
  items,
  setItems,
  setErrorMessage,
}: {
  items: HospitalRouteItemWithId[];
  setItems: (items: HospitalRouteItemWithId[]) => void;
  setErrorMessage: (message: string | null) => void;
}) => {
  const [dragging, setDragging] = useState(false);

  const handleDragStart = () => {
    setErrorMessage(null);
    setDragging(true);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    const activeItemIndex = active.data.current?.sortable.index;
    const overItemIndex = over?.data.current?.sortable.index;
    if (!isNil(activeItemIndex) && !isNil(overItemIndex) && activeItemIndex !== overItemIndex) {
      const newItems = arrayMove(items, activeItemIndex, overItemIndex);
      const validationError = validateRouteItems(newItems);

      if (validationError) {
        setErrorMessage(validationError);
      } else {
        setItems(newItems);
        setDragging(false);
      }
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <SortableContext strategy={verticalListSortingStrategy} items={items}>
        {items.map((item) => (
          <SortableHospitalRouteItem key={item.id} id={item.id} isAnimating={dragging} content={getRouteInfo(item)} />
        ))}
      </SortableContext>
    </DndContext>
  );
};

export const DestinationOrderDialog = ({ recommendation, onConfirm, onClose }: Props) => {
  const [modifiedHospitalRoutes, setModifiedHospitalRoutes] = useState<HospitalRouteItemWithId[] | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleConfirm = async () => {
    try {
      await onConfirm(recommendation, modifiedHospitalRoutes);
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error));
    }
  };

  const renderItems = () => {
    return modifiedHospitalRoutes ? (
      <ModifiableHospitalRouteItems
        items={modifiedHospitalRoutes}
        setErrorMessage={setErrorMessage}
        setItems={setModifiedHospitalRoutes}
      />
    ) : (
      <>
        {recommendation.hospital_route_items.map((item, index) => (
          <ReadonlyHospitalRouteItem key={index} content={getRouteInfo(item)} />
        ))}
      </>
    );
  };

  return (
    <CustomDialog fullWidth maxWidth="sm" open onClose={onClose}>
      <Stack sx={{ bgcolor: COLORS.DR_WHITE }}>
        <CustomDialogTitle onClose={onClose} title="Destination ordering" />
        <DialogContent
          dividers
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,

            boxSizing: "border-box",
            "&.MuiDialogContent-root": {
              padding: 3,
            },
          }}
        >
          <Stack
            sx={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              borderRadius: "8px",
              border: `1px solid ${COLORS.EXTRAORDINARY_ABUNDANCE_OF_TINGE}`,
              p: 2,
              bgcolor: COLORS.WHITE,
            }}
          >
            <Typography>Manual ordering</Typography>
            <CustomSwitch
              value={!!modifiedHospitalRoutes}
              onChange={(_, value) => {
                setModifiedHospitalRoutes(
                  value ? recommendation.hospital_route_items.map((item, index) => ({ id: index, ...item })) : null
                );
              }}
            />
          </Stack>
          {modifiedHospitalRoutes ? (
            <Alert severity="info">Check that this is the most suitable asset for this custom route</Alert>
          ) : null}
          {renderItems()}
        </DialogContent>
        <DialogActions sx={{ gap: 2 }}>
          {errorMessage ? <ErrorAlert>{errorMessage}</ErrorAlert> : null}
          <Button size="large" variant="contained" onClick={handleConfirm}>
            Confirm
          </Button>
        </DialogActions>
      </Stack>
    </CustomDialog>
  );
};
